/**
 * Renders release notes written in the tiny markdown subset the Owner Portal
 * accepts: paragraphs, `- ` bullets and `## ` headings. Deliberately no
 * markdown library — the notes are owner-authored, short, and this stays
 * safe (plain text nodes only). Ported from ../lims's
 * `components/ui/release-notes.tsx`.
 */
const LIST_ITEM = /^\s*[-*•]\s+/;
const HEADING = /^#{1,3}\s+/;

export interface ReleaseNotesProps {
  notes: string | null | undefined;
  className?: string;
  emptyText?: string;
}

export function ReleaseNotes({ notes, className, emptyText = 'No release notes were provided.' }: ReleaseNotesProps) {
  const text = notes?.replace(/\r\n/g, '\n').trim() ?? '';
  if (!text) return <p className={['text-sm text-ink3', className].filter(Boolean).join(' ')}>{emptyText}</p>;
  const blocks = text.split(/\n{2,}/);
  return (
    <div className={['space-y-3 text-sm leading-6 text-ink2', className].filter(Boolean).join(' ')}>
      {blocks.map((block, index) => {
        const lines = block.split('\n');
        if (lines.every((line) => LIST_ITEM.test(line))) {
          return (
            <ul key={index} className="list-disc space-y-1 pl-5">
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>{line.replace(LIST_ITEM, '')}</li>
              ))}
            </ul>
          );
        }
        if (lines.length === 1 && HEADING.test(block)) {
          return (
            <p key={index} className="font-display text-[15px] font-bold text-ink">
              {block.replace(HEADING, '')}
            </p>
          );
        }
        return (
          <p key={index} className="whitespace-pre-wrap">
            {block}
          </p>
        );
      })}
    </div>
  );
}
